import { motion } from "framer-motion";
import { Bell, Building2, FileText, FolderOpen, Home, MapPin, Target, TrendingUp, Wallet, Zap } from "lucide-react";
import type { TutorialScene } from "./learnAppData";

const sectionRing = "rounded-xl border border-blue-200 bg-white";

function MetricStrip({ scene }: { scene: TutorialScene }) {
  const metrics = scene.metrics ?? [];
  if (!metrics.length) return null;
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {metrics.map((m, i) => (
        <motion.div
          key={m.label}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + i * 0.08 }}
          className="rounded-lg border border-blue-200 bg-white px-2 py-1.5"
        >
          <p className="text-[10px] text-gray-500 truncate font-medium">{m.label}</p>
          <p className="text-xs font-bold text-gray-900 leading-tight">{m.value}</p>
        </motion.div>
      ))}
    </div>
  );
}

function Bars({ color = "bg-blue-600/70" }: { color?: string }) {
  return (
    <div className="flex items-end gap-0.5 h-10 mt-2">
      {[35, 55, 42, 70, 58, 80, 65, 88, 72, 90].map((h, i) => (
        <motion.div
          key={i}
          className={`flex-1 rounded-t ${color}`}
          initial={{ height: 0 }}
          animate={{ height: `${h}%` }}
          transition={{ delay: 0.2 + i * 0.03, duration: 0.3 }}
        />
      ))}
    </div>
  );
}

/** Realistic mini UI per page layout — ported from the app's TutorialPageMocks.tsx */
export function TutorialPageMock({ scene }: { scene: TutorialScene }) {
  switch (scene.layout) {
    case "dashboard":
      return (
        <div className="h-full flex flex-col gap-2 p-2.5 overflow-y-auto bg-white">
          <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-700 to-blue-900 text-white p-2.5 shrink-0">
            <p className="text-[11px] text-blue-100 font-medium">Good morning</p>
            <p className="text-sm sm:text-base font-bold leading-snug mt-0.5">Investment intelligence overview</p>
            <span className="mt-1.5 inline-block text-[10px] font-semibold rounded-full border border-white/30 bg-white/15 px-2.5 py-0.5">
              Bundle plan
            </span>
          </div>
          <MetricStrip scene={scene} />
          <div className="grid grid-cols-2 gap-1.5 shrink-0">
            {[
              { name: "WealthScope", cls: "from-blue-700 to-blue-900" },
              { name: "PortfolioIQ", cls: "from-emerald-700 to-emerald-900" },
              { name: "DealHunterIQ", cls: "from-purple-700 to-purple-900", full: true },
            ].map((c) => (
              <div
                key={c.name}
                className={`rounded-xl border border-blue-200 bg-gradient-to-br ${c.cls} text-white px-2.5 py-2 flex items-center justify-between min-w-0 ${
                  c.full ? "col-span-2" : ""
                }`}
              >
                <p className="text-xs font-bold leading-tight whitespace-nowrap">{c.name}</p>
                <p className="text-[10px] text-white/80 font-medium shrink-0">Open →</p>
              </div>
            ))}
          </div>
        </div>
      );

    case "wealth-hero":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-700 to-blue-900 text-white p-3 shrink-0">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-white/15 p-1.5">
                <TrendingUp size={14} />
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-blue-200">Net Worth</p>
                <p className="text-lg font-bold leading-none">{scene.metrics?.[0]?.value ?? "$1.24M"}</p>
              </div>
            </div>
            <div className="mt-2 flex justify-between text-center">
              {(scene.metrics ?? []).slice(0, 3).map((m) => (
                <div key={m.label}>
                  <p className="text-[8px] text-blue-300">{m.label}</p>
                  <p className="text-[11px] font-bold">{m.value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`${sectionRing} p-2 flex-1 min-h-0`}>
            <p className="text-[9px] font-semibold text-gray-500">12-Month Trend</p>
            <Bars />
          </div>
        </div>
      );

    case "cash-flow":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex items-center gap-2 shrink-0 px-2 py-1.5`}>
            <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center">
              <Wallet className="text-blue-600" size={14} />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-900">Cash Flow</p>
              <p className="text-[9px] text-gray-500">This month</p>
            </div>
          </div>
          <MetricStrip scene={scene} />
          <div className="grid grid-cols-2 gap-1.5 flex-1 min-h-0">
            <div className={`${sectionRing} p-2`}>
              <p className="text-[9px] text-emerald-600 font-medium">Income</p>
              <Bars color="bg-emerald-500/70" />
            </div>
            <div className={`${sectionRing} p-2`}>
              <p className="text-[9px] text-amber-600 font-medium">Expenses</p>
              <Bars color="bg-amber-500/70" />
            </div>
          </div>
        </div>
      );

    case "metric-grid":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-700 to-blue-900 text-white p-3 shrink-0 flex items-center gap-2">
            <div className="rounded-lg bg-white/15 p-1.5">
              <Zap size={14} />
            </div>
            <div>
              <p className="text-[9px] text-blue-200 uppercase tracking-wider">{scene.pageTitle}</p>
              <p className="text-base font-bold">{scene.metrics?.[0]?.value ?? "—"}</p>
            </div>
          </div>
          <MetricStrip scene={scene} />
          <div className={`${sectionRing} p-2 flex-1`}>
            <p className="text-[9px] text-gray-500 mb-1">Signal detail</p>
            <Bars />
          </div>
        </div>
      );

    case "property-list":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex items-center justify-between shrink-0 px-2 py-1.5`}>
            <div className="flex items-center gap-1.5">
              <Building2 className="text-blue-600" size={14} />
              <p className="text-[11px] font-semibold text-gray-900">Properties</p>
            </div>
            <span className="text-[9px] font-semibold bg-blue-600 text-white rounded-lg px-2 py-1">+ Add Property</span>
          </div>
          <MetricStrip scene={scene} />
          <div className="space-y-1.5 flex-1 overflow-hidden">
            {[
              { addr: "124 Oak St, Austin", val: "$420K" },
              { addr: "88 Pine Ave, Dallas", val: "$310K" },
              { addr: "15 Lake Rd, Houston", val: "$275K" },
            ].map((p) => (
              <div key={p.addr} className={`${sectionRing} px-2.5 py-2 flex items-center gap-2`}>
                <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                  <Home className="text-blue-600" size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold text-gray-900 truncate">{p.addr}</p>
                  <p className="text-[9px] text-gray-500">Multi-family · Occupied</p>
                </div>
                <p className="text-[11px] font-bold text-gray-900">{p.val}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case "performance":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex gap-1 shrink-0 p-1.5`}>
            {["Overview", "Financials", "Analysis"].map((t, i) => (
              <span
                key={t}
                className={`text-[9px] font-medium rounded-full px-2.5 py-1 ${i === 0 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}
              >
                {t}
              </span>
            ))}
          </div>
          <MetricStrip scene={scene} />
          <div className={`${sectionRing} p-2 flex-1`}>
            <p className="text-[9px] font-semibold text-gray-500">Portfolio returns</p>
            <Bars color="bg-sky-500/70" />
          </div>
        </div>
      );

    case "entities":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <MetricStrip scene={scene} />
          <div className="space-y-1.5 shrink-0">
            {["Oak Holdings LLC", "Personal", "Lakeview REIT LLC", "Riverside Partners LP"].map((e) => (
              <div key={e} className={`${sectionRing} px-2.5 py-2 flex items-center justify-between`}>
                <p className="text-[10px] font-semibold text-gray-900">{e}</p>
                <span className="text-[9px] text-blue-600 font-medium">3 props</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "calendar":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <MetricStrip scene={scene} />
          <div className={`${sectionRing} grid grid-cols-7 gap-0.5 p-1.5 flex-1 content-start`}>
            {Array.from({ length: 28 }).map((_, i) => (
              <div
                key={i}
                className={`aspect-square rounded text-[8px] flex items-center justify-center ${
                  [4, 11, 18, 22].includes(i) ? "bg-blue-600 text-white font-semibold" : "bg-gray-50 text-gray-400"
                }`}
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      );

    case "hunt":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex items-center gap-1.5 shrink-0 px-2 py-1.5`}>
            <Target className="text-blue-600" size={14} />
            <p className="text-[11px] font-semibold text-gray-900">{scene.pageTitle}</p>
          </div>
          <div className="rounded-lg border border-blue-200 bg-gray-50 h-6 px-2 flex items-center text-[9px] text-gray-500">
            Filter · Cap rate · Markets · Buying power
          </div>
          <MetricStrip scene={scene} />
          <div className="space-y-1 flex-1 overflow-hidden">
            {["$189K · 6.1% cap · Dallas", "$240K · 5.4% cap · Austin", "$155K · 7.0% cap · Houston"].map((row) => (
              <div key={row} className={`${sectionRing} px-2 py-1.5 text-[10px] text-gray-700`}>
                {row}
              </div>
            ))}
          </div>
        </div>
      );

    case "map":
      return (
        <div className="h-full relative overflow-hidden rounded-lg m-2 bg-gradient-to-br from-sky-100 via-emerald-50 to-sky-50 border border-blue-200">
          {[
            { t: "22%", l: "30%" },
            { t: "48%", l: "55%" },
            { t: "35%", l: "70%" },
            { t: "60%", l: "40%" },
          ].map((pin, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{ top: pin.t, left: pin.l }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 + i * 0.1 }}
            >
              <MapPin className="text-blue-600 drop-shadow" size={18} fill="#1d4ed8" />
            </motion.div>
          ))}
          <p className="absolute bottom-2 left-2 text-[9px] font-medium text-blue-900 bg-white/80 rounded border border-blue-200 px-1.5 py-0.5">
            Deal Map · 18 pins
          </p>
        </div>
      );

    case "pipeline":
      return (
        <div className="h-full flex gap-1.5 p-2 overflow-hidden">
          {["New", "Review", "Offer"].map((col, ci) => (
            <div key={col} className="flex-1 rounded-xl bg-gray-50 border border-blue-200 p-1.5">
              <p className="text-[9px] font-semibold text-gray-500 mb-1 px-0.5">{col}</p>
              {Array.from({ length: 2 - (ci === 2 ? 1 : 0) }).map((_, i) => (
                <div key={i} className="rounded-lg bg-white border border-blue-200 p-1.5 mb-1 text-[9px] text-gray-700">
                  Deal {ci + 1}.{i + 1}
                </div>
              ))}
            </div>
          ))}
        </div>
      );

    case "analytics":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <MetricStrip scene={scene} />
          <div className={`${sectionRing} p-2 flex-1`}>
            <p className="text-[9px] text-gray-500">Pipeline pace</p>
            <Bars color="bg-blue-900/60" />
          </div>
        </div>
      );

    case "reports":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex items-center gap-1.5 px-2 py-1.5`}>
            <FileText className="text-blue-600" size={14} />
            <p className="text-[11px] font-semibold">Reports & Data</p>
          </div>
          <div className={`${sectionRing} p-2 space-y-1.5 shrink-0`}>
            {["Module: WealthScope", "Type: Net Worth Summary", "Format: PDF"].map((row) => (
              <div key={row} className="rounded-lg border border-blue-200 bg-gray-50 px-2 py-1.5 text-[10px] text-gray-700">
                {row}
              </div>
            ))}
            <div className="mt-2 rounded-xl border border-blue-600 bg-blue-600 text-white text-center text-[10px] font-semibold py-2">
              Generate Report
            </div>
          </div>
        </div>
      );

    case "documents":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex items-center gap-1.5 px-2 py-1.5`}>
            <FolderOpen className="text-blue-600" size={14} />
            <p className="text-[11px] font-semibold">Documents</p>
          </div>
          <div className="space-y-1.5 shrink-0">
            {[
              "Closing_124_Oak.pdf",
              "Bank_Statement_Jun.csv",
              "Lease_88_Pine.pdf",
              "Insurance_Policy_2025.pdf",
              "Appraisal_88_Pine.pdf",
              "Title_Report_124_Oak.pdf",
              "W2_2024.pdf",
              "Property_Tax_Bill.pdf",
            ].map((f) => (
              <div key={f} className={`${sectionRing} px-2.5 py-2.5 text-[10px] text-gray-700 flex items-center gap-2`}>
                <FileText size={12} className="text-gray-400 shrink-0" />
                <span className="truncate">{f}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "alerts":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className={`${sectionRing} flex items-center gap-1.5 px-2 py-1.5`}>
            <Bell className="text-blue-600" size={14} />
            <p className="text-[11px] font-semibold">Alerts & Insights</p>
          </div>
          <div className="inline-flex bg-gray-100 rounded-lg p-0.5 self-start border border-blue-200">
            {["All", "High", "Low"].map((t, i) => (
              <span
                key={t}
                className={`text-[9px] px-2 py-1 rounded-md font-medium ${i === 0 ? "bg-white text-blue-600 shadow-sm" : "text-gray-500"}`}
              >
                {t}
              </span>
            ))}
          </div>
          <div className="space-y-1.5 shrink-0">
            {[
              { t: "Lease renewal in 21 days", c: "bg-amber-50 border-blue-200" },
              { t: "Cash flow up 8% MoM", c: "bg-emerald-50 border-blue-200" },
              { t: "DSCR dropped below 1.2 on 124 Oak St", c: "bg-red-50 border-blue-200" },
              { t: "New comparable sale near Austin, TX", c: "bg-sky-50 border-blue-200" },
              { t: "Buying power increased to $912K", c: "bg-emerald-50 border-blue-200" },
            ].map((a) => (
              <div key={a.t} className={`rounded-xl border px-2.5 py-2 text-[10px] ${a.c}`}>
                {a.t}
              </div>
            ))}
          </div>
        </div>
      );

    case "subscriptions":
      return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-y-auto">
          <div className="rounded-xl border border-blue-200 bg-sky-50 p-3">
            <p className="text-[9px] text-blue-600 font-semibold uppercase">Current plan</p>
            <p className="text-sm font-bold text-gray-900">VaWeNa Bundle</p>
            <p className="text-[9px] text-gray-500 mt-0.5">Active · renews Oct 1</p>
          </div>
          <div className="grid grid-cols-2 gap-1.5 shrink-0">
            {["WealthScope", "PortfolioIQ"].map((p) => (
              <div key={p} className={`${sectionRing} p-2`}>
                <p className="text-[10px] font-semibold text-gray-900">{p}</p>
                <p className="text-[9px] text-emerald-600 mt-1">Included</p>
              </div>
            ))}
          </div>
          <div className={`${sectionRing} p-2 space-y-1.5 shrink-0`}>
            <p className="text-[9px] font-semibold text-gray-500 uppercase">Billing</p>
            {["Next invoice: $34.99 on Oct 1", "Payment method: Visa •••• 4242", "Manage or cancel anytime"].map((row) => (
              <div key={row} className="rounded-lg border border-blue-100 bg-gray-50 px-2 py-1.5 text-[9px] text-gray-700">
                {row}
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return (
        <div className="h-full p-3">
          <MetricStrip scene={scene} />
          <div className={`${sectionRing} p-2 mt-2`}>
            <Bars />
          </div>
        </div>
      );
  }
}
